import { portaledDropdownClassName } from 'LuxKit/Dropdown/const'
import React, { useEffect, useRef } from 'react'

function nodeIsInModal(node: HTMLElement | null, box: HTMLElement) {
  if (!node) return false
  if (node.classList.contains('ReactModalPortal') && !node.contains(box)) return true
  return nodeIsInModal(node.parentElement, box)
}

function nodeIsInPortaledDropdown(node: HTMLElement | null, box: HTMLElement) {
  if (!node) return false
  if (node.classList.contains(portaledDropdownClassName) && !node.contains(box)) return true
  return nodeIsInPortaledDropdown(node.parentElement, box)
}

interface Options {
  enabled: boolean
  movementTolerancePx: number
  excludeModalClicks: boolean
  excludePortaledDropdownClicks: boolean
}

const defaultOptions: Options = {
  enabled: true,
  movementTolerancePx: 20,
  excludeModalClicks: false,
  excludePortaledDropdownClicks: false,
}

export default function useClickOutsideBox(
  ref: React.RefObject<HTMLElement | null>,
  callback: (e: MouseEvent) => void,
  options: Partial<Options> = {},
) {
  const { enabled, movementTolerancePx, excludeModalClicks, excludePortaledDropdownClicks } = {
    ...defaultOptions,
    ...options,
  }
  const enabledRef = useRef<Boolean>(enabled)
  const callbackRef = useRef<any>(null)
  const mouseUpPosRef = useRef({ x: 0, y: 0 })
  const mouseDownPosRef = useRef({ x: 0, y: 0 })

  callbackRef.current = callback
  enabledRef.current = enabled

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const diffX = Math.abs(
        mouseUpPosRef.current.x - mouseDownPosRef.current.x,
      )
      const diffY = Math.abs(
        mouseUpPosRef.current.y - mouseDownPosRef.current.y,
      )

      if (
        enabledRef.current &&
        ref.current &&
        diffX < movementTolerancePx &&
        diffY < movementTolerancePx &&
        !ref.current.contains(e.target as Node) &&
        (!excludeModalClicks || !nodeIsInModal(e.target as HTMLElement, ref.current)) &&
        (!excludePortaledDropdownClicks || !nodeIsInPortaledDropdown(e.target as HTMLElement, ref.current))
      ) {
        callbackRef.current(e)
      }
    }

    function handleMouseUp(e: MouseEvent) {
      mouseUpPosRef.current.x = e.pageX
      mouseUpPosRef.current.y = e.pageY
    }

    function handleMouseDown(e: MouseEvent) {
      mouseDownPosRef.current.x = e.pageX
      mouseDownPosRef.current.y = e.pageY
    }

    window.addEventListener('click', handleClick, true)
    window.addEventListener('mousedown', handleMouseDown, true)
    window.addEventListener('mouseup', handleMouseUp, true)
    return () => {
      window.removeEventListener('click', handleClick, true)
      window.removeEventListener('mousedown', handleMouseDown, true)
      window.removeEventListener('mouseup', handleMouseUp, true)
    }
  }, [ref, movementTolerancePx, excludeModalClicks, excludePortaledDropdownClicks])
}
